import * as React from 'react'
import { Box, Tooltip, Alert, TextField } from '@mui/material'
import AddIcon from 'components/add-icon'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Close'
import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  type GridRowsProp,
  type GridRowModesModel,
  GridRowModes,
  DataGrid,
  type GridColDef,
  type GridEventListener,
  type GridRowId,
  GridRowEditStopReasons,
  type GridSlotProps,
  Toolbar,
  ToolbarButton,
  type GridSlots,
  GridActionsCellItem,
} from '@mui/x-data-grid'
import { v4 as uuidv4 } from 'uuid'

import { useLocation } from 'react-router'

import colors from 'common/colors'

import { createEquipment, deleteEquipment, editEquipment } from 'api/equipment'

import { difference } from 'es-toolkit/compat'
import { useDispatch, useSelector } from 'react-redux'
import {
  type EquipmentFetchModel,
  type EquipmentModelAPIRes,
  fetchEquipmentModels,
} from 'api/equipment-model'

import MakeSelectorComponent from 'components/farm-settings/equipment/make-selector'

import ModelSelectorComponent from './model-selector'

import type { AppState } from 'state/index'

import type { EquipmentModel } from 'state/equipment'

import type { HardwareModel } from 'state/hardware'

import { TEST_IDS } from 'test-ids'

declare module 'components/farm-settings/equipment/index.js' {
  export type EquipmentErrors = {
    nickname: string
    model: string
    make: string
  }
  export type BrandOption = {
    id: string
    name: string
    inputValue: string | undefined
  }
  export type ModelOption = {
    id: string | null
    inputValue: string | undefined
    name: string
    makeBrandId: string | null
    equipmentType: string
  }
  export type EquipmentRowData = {
    id: string
    brandOption: BrandOption | null
    modelOption: Partial<ModelOption> | null
    nickname: string
    equipmentType: string | null
    timeDelaySec: number
    year: number | null
    ymId: string
    msId: string
    headerHeightId: string
  }

  export type EquipmentComponentProps = {
    hideTimeDelay?: boolean
    enableEqNextButton?: Function
  }
  export function EquipmentComponent(
    props: EquipmentComponentProps,
  ): ReactElement<any, any>
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void
  }
}

const EquipmentsComponentWrapper = (props: EquipmentComponentProps) => {
  return (
    <Box
      sx={{
        width: '100%',
        '& .Mui-error': {
          backgroundColor: `rgba(126,10,15, 0.1)`,
          color: '#7e0a0f',
          '& .MuiInputBase-input': {
            borderColor: '#7e0a0f',
          },
        },
      }}
    >
      <EquipmentsComponent {...props} />
    </Box>
  )
}

export default EquipmentsComponentWrapper

const centeredEditCellSx = {
  alignItems: 'center',
  display: 'flex',
  height: 40,
  overflow: 'visible',
  py: 0.5,
  width: '100%',
}

function EquipmentsComponent(
  props: EquipmentComponentProps,
): ReactElement<any, any> {
  const [rows, setRows] = useState<EquipmentRowData[]>([])
  const { t } = useTranslation()

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  )
  const { pathname } = useLocation()

  const dispatch = useDispatch()
  const hideTimeDelay = props?.hideTimeDelay

  const permissions = useSelector(
    (state) => (state as AppState).account.permissions,
  )
  const equipment = useSelector(
    (state) => (state as AppState).equipment.collection,
  )
  const brands = useSelector((state) => (state as AppState).brand.brands)
  const hardware = useSelector((state: AppState) => state.hardware.collection)

  const [disableDeleteItem, setDisableDeleteItem] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [equipmentModels, setEquipmentModels] = useState({})
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([])
  const [rowSelected, setSelectedRow] = useState<string | number>(-1)
  const [addButtonClicked, toggleAddRow] = useState(false)
  const [brandAdded, setBrandAdded] = useState(false)
  const [showHardware, setShowHardware] = useState(false)

  const [editObj, setEditObj] = useState({})
  const [touchedFields, setTouchedFields] = useState<
    Record<string, Set<string>>
  >({})

  const markTouched = useCallback((rowId: string, field: string) => {
    setTouchedFields((prev) => {
      const existing = prev[rowId] ? new Set(prev[rowId]) : new Set<string>()
      existing.add(field)
      return { ...prev, [rowId]: existing }
    })
  }, [])

  const hasDuplicateNickname = useMemo(() => {
    const nicknames = rows
      .map((r) => r.nickname)
      .filter((nickname) => typeof nickname === 'string' && nickname !== '')
    return nicknames.length !== new Set(nicknames).size
  }, [rows])

  const columns: GridColDef[] = [
    {
      field: 'nickname',
      headerName: t('combine_name'),
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        const isTouched = !!touchedFields[params.id as string]?.has('nickname')
        const unique = rows.filter(
          (item) =>
            item.nickname.trim() === params.row.nickname?.trim() &&
            item.id !== params.row.id,
        )
        const isDuplicate = unique.length > 0
        const hasError =
          isTouched && (!params.row.nickname?.trim() || isDuplicate)
        return (
          <Box sx={centeredEditCellSx}>
            <TextField
              value={params.row.nickname ?? ''}
              onChange={(e) => {
                const value = e.target.value
                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id ? { ...row, nickname: value } : row,
                  ),
                )
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'nickname',
                  value,
                })
              }}
              onBlur={() => markTouched(params.id as string, 'nickname')}
              error={hasError}
              label={t('enter_unique_combine_name')}
              slotProps={{
                htmlInput: {
                  'data-testid': `${TEST_IDS.equipmentNicknameInput}-${params.id}`,
                },
              }}
              size="small"
              fullWidth
              autoFocus={params.hasFocus}
            />
          </Box>
        )
      },
    },
    {
      field: 'brandOption',
      headerName: t('make'),
      flex: 1,
      editable: true,
      renderCell: (params) => {
        return params.row.brandOption?.name || ''
      },
      preProcessEditCellProps: (params) => {
        const value = params.props.value
        let error = false
        if (!value) {
          error = true
        }

        return { ...params.props, error }
      },
      renderEditCell: (params) => {
        const isTouched =
          !!touchedFields[params.id as string]?.has('brandOption')
        const hasError = isTouched && !params.row.brandOption?.id
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 40,
              width: '100%',
            }}
          >
            <MakeSelectorComponent
              onValueChange={(newBrandOption) => {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'brandOption',
                  value: newBrandOption,
                })
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'modelOption',
                  value: null,
                })
                onMakeChange(params.row, newBrandOption)
              }}
              value={params.row.brandOption}
              row={params.row}
              onBrandAdded={handleBrandAdded}
              id={params.id}
              property={params.field}
              error={hasError}
              testId={`${TEST_IDS.equipmentMakeSelect}-${params.id}`}
              onBlur={() => markTouched(params.id as string, 'brandOption')}
            />
          </Box>
        )
      },
    },
    {
      field: 'modelOption',
      headerName: t('model'),
      type: 'string',
      flex: 1,
      editable: true,
      renderCell: (params) => {
        return equipmentModels[params.row.modelOption?.id!]?.name || ''
      },
      preProcessEditCellProps: (params) => {
        const value = params.props.value
        let error = false
        if (!value) {
          error = true
        }

        return { ...params.props, error }
      },
      renderEditCell: (params) => {
        const isTouched =
          !!touchedFields[params.id as string]?.has('modelOption')
        const hasError = isTouched && !params.row.modelOption?.id
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 40,
              width: '100%',
            }}
          >
            <ModelSelectorComponent
              makeBrandId={params.row?.brandOption?.id || null}
              equipmentType={params.row.equipmentType}
              value={params.row.modelOption}
              onValueChange={(newModelOption) => {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'modelOption',
                  value: newModelOption,
                })
                onModelChange(params.row, newModelOption)
              }}
              disabled={params.row?.brandOption ? false : true}
              id={params.id}
              property={params.field}
              error={hasError}
              testId={`${TEST_IDS.equipmentModelSelect}-${params.id}`}
              onBlur={() => markTouched(params.id as string, 'modelOption')}
            />
          </Box>
        )
      },
    },

    {
      field: 'year',
      headerName: t('production_year'),
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        return (
          <Box sx={centeredEditCellSx}>
            <TextField
              value={params.row.year ?? ''}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id
                      ? { ...row, year: value === '' ? null : Number(value) }
                      : row,
                  ),
                )
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'year',
                  value: value === '' ? null : Number(value),
                })
              }}
              onBlur={() => markTouched(params.id as string, 'year')}
              label={t('enter_valid_year')}
              slotProps={{
                htmlInput: {
                  'data-testid': `${TEST_IDS.equipmentYearInput}-${params.id}`,
                },
              }}
              size="small"
              fullWidth
              autoFocus={params.hasFocus}
            />
          </Box>
        )
      },
    },
    {
      field: 'timeDelaySec',
      headerName: t('time_delay'),
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        return (
          <Box sx={centeredEditCellSx}>
            <TextField
              value={params.row.timeDelaySec ?? ''}
              onChange={(e) => {
                const value = e.target.value
                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id
                      ? { ...row, timeDelaySec: Number(value) }
                      : row,
                  ),
                )
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'timeDelaySec',
                  value: value === '' ? null : Number(value),
                })
              }}
              onBlur={() => markTouched(params.id as string, 'timeDelaySec')}
              label={t('enter_time_delay')}
              slotProps={{
                htmlInput: {
                  'data-testid': `${TEST_IDS.equipmentTimeDelayInput}-${params.id}`,
                },
              }}
              size="small"
              fullWidth
              autoFocus={params.hasFocus}
            />
          </Box>
        )
      },
    },
    {
      field: 'ymId',
      headerName: t('ym_id'),
      flex: 1,
      type: 'string',
    },
    {
      field: 'msId',
      headerName: t('ms_id'),
      flex: 1,
      type: 'string',
    },
    {
      field: 'headerHeightId',
      headerName: t('header_height_sensor_id'),
      flex: 1,
      type: 'string',
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions'),
      flex: 1,
      cellClassName: 'actions',
      headerClassName: 'actions',
      getActions: (params) => {
        const isInEditMode =
          rowModesModel[params.id]?.mode === GridRowModes.Edit

        const isAnyRowEditing = Object.values(rowModesModel).some(
          (model) => model.mode === GridRowModes.Edit,
        )

        const unique = rows.filter(
          (item) =>
            item.nickname.trim() === params.row.nickname?.trim() &&
            item.id !== params.row.id,
        )
        const isDuplicate = unique.length > 0

        const hasError =
          !params.row.nickname?.trim() ||
          isDuplicate ||
          !params.row.brandOption?.id ||
          !params.row.modelOption?.id

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={
                <SaveIcon
                  titleAccess={t('save')}
                  sx={{
                    color: hasError ? 'action.disabled' : colors.brandLight,
                  }}
                />
              }
              label={t('save')}
              data-testid={`${TEST_IDS.equipmentSaveButton}-${params.id}`}
              disabled={hasError || !canEdit}
              onClick={() => handleSave(params.id)}
            />,

            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon titleAccess={t('cancel')} />}
              label={t('cancel')}
              data-testid={`${TEST_IDS.equipmentCancelButton}-${params.id}`}
              onClick={() => handleCancel(params.id)}
            />,
          ]
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon titleAccess={t('edit')} />}
            label={t('edit')}
            data-testid={`${TEST_IDS.equipmentEditButton}-${params.id}`}
            onClick={() => handleEdit(params.id)}
            disabled={!canEdit || isAnyRowEditing}
          />,

          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon titleAccess={t('delete')} />}
            label={t('delete')}
            data-testid={`${TEST_IDS.equipmentDeleteButton}-${params.id}`}
            onClick={() => handleDelete(params.id)}
            disabled={!canEdit || isAnyRowEditing}
          />,
        ]
      },
    },
  ]

  useEffect(() => {
    if (brandAdded) {
      let addedBrand, rowIndex
      rowIndex = rows.findIndex((row) => row.id === rowSelected)
      if (rowIndex !== -1) {
        addedBrand = brands.find(
          (brand) =>
            brand.name.trim() === rows[rowIndex]?.brandOption?.name?.trim(),
        )
        if (addedBrand) {
          const updatedRows = [...rows]
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            brandOption: {
              id: addedBrand.id,
              name: addedBrand.name,
              inputValue: '',
            },
          }
          setRows(updatedRows)
        }
      }
      setBrandAdded(false)
    } else {
      const _brandOptions: BrandOption[] = []
      brands.forEach((item) => {
        _brandOptions.push({
          id: item.id,
          name: item.name,
          inputValue: '',
        })
      })
      setBrandOptions(_brandOptions)
    }
  }, [brands, brandAdded, rowSelected])

  const handleBrandAdded = (newValue: boolean) => {
    setBrandAdded(newValue)
  }

  useEffect(() => {
    setCanEdit(permissions.includes('farm:write'))
  }, [permissions])

  useEffect(() => {
    if (
      pathname !== '/farmSetup/combines' &&
      props?.enableEqNextButton != undefined
    ) {
      setShowHardware(false)
      if (equipment && equipment.length) {
        props?.enableEqNextButton(true)
      } else {
        props?.enableEqNextButton(false)
      }
    }
  }, [equipment, pathname])

  const EditToolbar = (props: GridSlotProps['toolbar']) => {
    const { setRows: _, setRowModesModel: __, ...other } = props

    const addEmptyRow = useCallback(
      (e) => {
        if (disableDeleteItem) {
          setDisableDeleteItem(false)
        }

        if (addButtonClicked) {
          e.preventDefault()
        } else {
          toggleAddRow(true)
          const id = uuidv4()
          setSelectedRow(id)
          const newRow: EquipmentRowData = {
            id,
            brandOption: null,
            modelOption: null,
            nickname: '',
            equipmentType: 'Combine',
            timeDelaySec: 10,
            year: null,
            ymId: '',
            msId: '',
            headerHeightId: '',
          }
          setRows((prevRows) => [...prevRows, newRow])
          setEditObj(newRow)
          setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: 'nickname' },
          }))
        }
      },
      [rows, brands, disableDeleteItem],
    )

    return (
      <Toolbar {...other}>
        <Tooltip title={t('add_equipment')}>
          <ToolbarButton
            data-testid={TEST_IDS.addEquipmentButton}
            onClick={addEmptyRow}
          >
            <AddIcon fontSize="small" />
          </ToolbarButton>
        </Tooltip>
      </Toolbar>
    )
  }

  useEffect(() => {
    if (rowSelected !== -1 && Object.keys(editObj).length > 0) {
      return
    }

    const missingModelIds: (string | undefined)[] = difference(
      equipment
        .filter((e) => e.equipmentModelId)
        .map((e) => (e as EquipmentModel)?.equipmentModelId?.toString()),
      Object.keys(equipmentModels),
    )

    if (missingModelIds.length > 0) {
      const reqIds: EquipmentFetchModel = { ids: missingModelIds }
      fetchEquipmentModels(reqIds).then(
        (missingModels: EquipmentModelAPIRes[]) => {
          for (const missingModel of missingModels) {
            equipmentModels[missingModel.id] = missingModel
          }

          setEquipmentModels({ ...equipmentModels })
        },
      )
    }
    const _rowData: EquipmentRowData[] = []
    equipment.forEach((item) => {
      const brandOption = brandOptions.find((brand) => {
        return brand.id === item.makeBrandId
      })
      const modelOption: Partial<ModelOption> = {
        id: item.equipmentModelId,
      }

      const ym: HardwareModel | undefined = hardware.find((ym) => {
        return (
          ym.equipmentId === item.id &&
          ym.hardwareType.toLowerCase() === 'yieldmonitor'
        )
      })

      const ms: HardwareModel | undefined = hardware.find((ym) => {
        return (
          ym.equipmentId === item.id &&
          ym.hardwareType.toLowerCase() === 'moisturesensor'
        )
      })

      const headerHeight: HardwareModel | undefined = hardware.find((ym) => {
        return (
          ym.equipmentId === item.id &&
          ym.hardwareType.toLowerCase() === 'headerheightsensor'
        )
      })

      _rowData.push({
        id: item.id,
        brandOption: brandOption || null,
        modelOption: modelOption || null,
        nickname: item.nickname,
        equipmentType: item.equipmentType,
        timeDelaySec: item.timeDelaySec,
        year: item.year,
        msId: ms?.farmtrxHardwareId || '',
        ymId: ym?.farmtrxHardwareId || '',
        headerHeightId: headerHeight?.farmtrxHardwareId || '',
      })
    })

    setRows(_rowData)
  }, [equipment, brandOptions, hardware])

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleEdit = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }))
    },
    [setRowModesModel],
  )

  const handleSave = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }))
    },
    [setRowModesModel],
  )

  const handleCancel = useCallback(
    (id: GridRowId) => {
      const isPendingAddRow = addButtonClicked && id === rowSelected
      setTouchedFields((prev) => {
        const { [id as string]: _, ...rest } = prev
        return rest
      })
      if (isPendingAddRow) {
        toggleAddRow(false)
      }
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }))

      setRows((prevRows) => {
        if (isPendingAddRow) {
          return prevRows.filter((row) => row.id !== id)
        }
        return prevRows
      })
    },
    [addButtonClicked, rowSelected, rows, setRowModesModel],
  )

  const handleDelete = useCallback(
    (id: GridRowId) => {
      const savedRowCount = rows.length - (addButtonClicked ? 1 : 0)
      if (savedRowCount > 1) {
        //@ts-expect-error
        dispatch(deleteEquipment(id))
        toggleAddRow(false)
        if (rowSelected === id) setEditObj({})
      } else {
        setDisableDeleteItem(true)
      }
    },
    [addButtonClicked, rows],
  )

  //Function to store the Equipment data into the DB
  const saveData = useCallback(
    async (dataToSave) => {
      const obj = {
        id: dataToSave.id,
        makeBrandId: dataToSave.brandOption?.id,
        equipmentModelId: dataToSave.modelOption?.id,
        nickname: dataToSave.nickname,
        equipmentType: dataToSave.equipmentType,
        timeDelaySec: Number(dataToSave.timeDelaySec),
        year:
          dataToSave.year === null || dataToSave.year === ''
            ? null
            : Number(dataToSave.year),
      }
      if (addButtonClicked && dataToSave.id === rowSelected) {
        //@ts-expect-error
        await dispatch(createEquipment(obj))
      } else {
        //@ts-expect-error
        await dispatch(editEquipment(dataToSave.id, obj))
      }
      setSelectedRow(-1)
      toggleAddRow(false)
      setEditObj({})
      return dataToSave
    },
    [addButtonClicked, rowSelected],
  )

  const onMakeChange = useCallback(
    (updatedRow, newSelectedBrandOption) => {
      const newRows = rows.map((row) => {
        if (row.id === updatedRow.id) {
          return {
            ...row,
            brandOption: newSelectedBrandOption || null,
            modelOption: null,
          }
        }
        return row
      })
      setRows(newRows)
      setSelectedRow(updatedRow.id)
    },
    [rows],
  )

  const onModelChange = useCallback(
    (updatedRow, newModelOption) => {
      const newRows = rows.map((row) => {
        if (row.id === updatedRow.id) {
          return {
            ...row,
            modelOption: newModelOption,
          }
        }
        return row
      })

      setRows(newRows)
    },
    [rows],
  )

  const processRowUpdate = async (
    newRow: EquipmentRowData,
    oldRow: EquipmentRowData,
  ) => {
    const isDuplicate = rows.some(
      (item) =>
        item.nickname.trim() === newRow?.nickname.trim() &&
        item.id !== newRow.id,
    )
    if (isDuplicate) {
      setRowModesModel((prev) => ({
        ...prev,
        [newRow?.id]: { mode: GridRowModes.Edit },
      }))
      return oldRow
    }
    const result = await saveData(newRow)
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === result.id ? result : row)),
    )
    return result
  }

  return (
    <Box
      sx={{
        m: pathname === '/farmSetup/combines' ? 2 : 0,
        width: { md: '70vw' },

        '& .actions': {
          color: 'text.secondary',
          position: 'sticky',
          right: 0,
          zIndex: 1,
          backgroundColor: colors.white,
        },
        '& .MuiDataGrid-columnHeader.actions': {
          position: 'sticky',
          right: 0,
          zIndex: 2,
          backgroundColor: colors.white,
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      {pathname === '/farmSetup/combines' ? (
        <h1>{t('combines')}</h1>
      ) : (
        <h1> {t('select_equipment')}</h1>
      )}
      {disableDeleteItem ? (
        <Alert
          severity="error"
          data-testid={TEST_IDS.equipmentDeleteDisabledAlert}
        >
          {t('delete_item_disable_message', { item: t('equipment') })}
        </Alert>
      ) : null}
      {hasDuplicateNickname ? (
        <Alert
          severity="error"
          data-testid={TEST_IDS.equipmentDuplicateNameAlert}
        >
          {t('equipment_same_nickname_error_message')}
        </Alert>
      ) : null}

      <DataGrid
        data-testid={TEST_IDS.equipmentTable}
        sx={{
          flex: 1,
          maxHeight: 400,
          '& .MuiDataGrid-main': { overflowY: 'auto' },
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            display: 'flex',
          },
          '& .MuiDataGrid-cell--editing': {
            alignItems: 'center',
            overflow: 'visible',
            py: 0.5,
          },
          '& .MuiDataGrid-cell--editing .MuiFormControl-root': {
            my: 0,
          },
          '& .MuiInputBase-root': {
            alignItems: 'center',
          },
        }}
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        columnVisibilityModel={{
          timeDelaySec: !hideTimeDelay,
          msId: showHardware,
          ymId: showHardware,
          headerHeightId: showHardware,
        }}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error(error)
        }}
        showToolbar
        slots={{
          ...(canEdit && { toolbar: EditToolbar as GridSlots['toolbar'] }),
        }}
        slotProps={{
          toolbar: {
            // Only pass standard props to avoid warnings
          },
        }}
      />
    </Box>
  )
}
